/**
 * 配信シリーズ API hooks.
 *
 * GET/POST/PATCH/DELETE /api/delivery-series
 */

import { RestApiError, restClient } from "lib/rest-client";
import { useCallback, useEffect, useState } from "react";

export type DeliverySeriesStatus = "active" | "archived";

export type DeliverySeriesStats = {
  deliveryCount: number;
  totalRecipients: number;
  totalReplies: number;
  unresponded: number;
  inProgress: number;
  completed: number;
  responseRate: number;
  lastSentAt: string | null;
};

export type DeliverySeries = {
  id: string;
  name: string;
  description: string | null;
  status: DeliverySeriesStatus;
  talentId: string | null;
  talentName: string | null;
  focusMatterId: string | null;
  focusMatterTitle: string | null;
  createdBy: string;
  creatorName: string | null;
  stats: DeliverySeriesStats;
  createdAt: string;
  updatedAt: string;
};

type DeliverySeriesListResponse = {
  items: DeliverySeries[];
  total: number;
  page: number;
  limit: number;
};

export type CreateDeliverySeriesInput = {
  name: string;
  description?: string | null;
  talentId?: string | null;
  focusMatterId?: string | null;
};

export type UpdateDeliverySeriesInput = {
  name?: string;
  description?: string | null;
  status?: DeliverySeriesStatus;
  talentId?: string | null;
  focusMatterId?: string | null;
};

/**
 * シリーズステータスの日本語ラベル
 * @param {DeliverySeriesStatus} status ステータス
 * @return {string} 表示ラベル
 */
export function deliverySeriesStatusLabel(
  status: DeliverySeriesStatus,
): string {
  return status === "active" ? "active" : "archived";
}

export type UseDeliverySeriesListOptions = {
  page?: number;
  limit?: number;
  status?: DeliverySeriesStatus | null;
  search?: string | null;
};

/**
 * シリーズ一覧取得 hook
 * @param {UseDeliverySeriesListOptions} options ページング/絞込
 * @return {object} items, total, loading, error, refetch
 */
export function useDeliverySeriesList(
  options: UseDeliverySeriesListOptions = {},
): {
  items: DeliverySeries[];
  total: number;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const { page = 1, limit = 50, status, search } = options;
  const [items, setItems] = useState<DeliverySeries[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: String(limit),
      };
      if (status) params.status = status;
      if (search) params.search = search;
      const response = await restClient.get<DeliverySeriesListResponse>(
        "/delivery-series",
        params,
      );
      setItems(response.items);
      setTotal(response.total);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [page, limit, status, search]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { items, total, loading, error, refetch: fetchList };
}

/**
 * 単一シリーズ取得 hook
 * @param {string|null} seriesId シリーズ ID
 * @return {object} series, loading, error, refetch
 */
export function useDeliverySeries(seriesId: string | null): {
  series: DeliverySeries | null;
  loading: boolean;
  error: RestApiError | null;
  refetch: () => Promise<void>;
} {
  const [series, setSeries] = useState<DeliverySeries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<RestApiError | null>(null);

  const fetchOne = useCallback(async () => {
    if (!seriesId) {
      setSeries(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.get<DeliverySeries>(
        `/delivery-series/${seriesId}`,
      );
      setSeries(response);
    } catch (e) {
      setError(toApiError(e));
    } finally {
      setLoading(false);
    }
  }, [seriesId]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { series, loading, error, refetch: fetchOne };
}

/**
 * シリーズ新規作成 hook
 * @return {object} create, loading, error
 */
export function useCreateDeliverySeries(): {
  create: (input: CreateDeliverySeriesInput) => Promise<DeliverySeries>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const create = useCallback(async (input: CreateDeliverySeriesInput) => {
    setLoading(true);
    setError(null);
    try {
      const response = await restClient.post<DeliverySeries>(
        "/delivery-series",
        input,
      );
      return response;
    } catch (e) {
      const err = toApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { create, loading, error };
}

/**
 * シリーズ更新 hook
 * @return {object} update, loading, error
 */
export function useUpdateDeliverySeries(): {
  update: (
    seriesId: string,
    input: UpdateDeliverySeriesInput,
  ) => Promise<DeliverySeries>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const update = useCallback(
    async (seriesId: string, input: UpdateDeliverySeriesInput) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.patch<DeliverySeries>(
          `/delivery-series/${seriesId}`,
          input,
        );
        return response;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { update, loading, error };
}

/**
 * シリーズ削除 hook
 * @return {object} remove, loading, error
 */
export function useDeleteDeliverySeries(): {
  remove: (seriesId: string) => Promise<void>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const remove = useCallback(async (seriesId: string) => {
    setLoading(true);
    setError(null);
    try {
      await restClient.delete<unknown>(`/delivery-series/${seriesId}`);
    } catch (e) {
      const err = toApiError(e);
      setError(err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { remove, loading, error };
}

/**
 * 既存配信をシリーズに加入させる hook
 * @return {object} attach, loading, error
 */
export function useAttachDeliveriesToSeries(): {
  attach: (seriesId: string, deliveryIds: string[]) => Promise<number>;
  loading: boolean;
  error: RestApiError | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<RestApiError | null>(null);

  const attach = useCallback(
    async (seriesId: string, deliveryIds: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const response = await restClient.post<{ attachedCount: number }>(
          `/delivery-series/${seriesId}/attach-deliveries`,
          { deliveryIds },
        );
        return response.attachedCount;
      } catch (e) {
        const err = toApiError(e);
        setError(err);
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { attach, loading, error };
}

/**
 * 任意の error を RestApiError に統一する
 * @param {unknown} e 発生した error
 * @return {RestApiError} ラップされた error
 */
function toApiError(e: unknown): RestApiError {
  if (e instanceof RestApiError) return e;
  return new RestApiError(
    e instanceof Error ? e.message : "Unknown error",
    500,
  );
}
